import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { HasParentProductId } from "@insite/client-framework/Components/ParentProductContext";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import ProductPriceSavingsMessage from "@insite/content-library/Components/ProductPriceSavingsMessage";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: ProductContextModel | CartLineModel;
    currencySymbol?: string;
    showLabel?: boolean;
    showUnitOfMeasure?: boolean;
    showPack?: boolean;
    showSavings?: boolean;
    showSavingsAmount?: boolean;
    showSavingsPercent?: boolean;
    showInvalidPriceMessage?: boolean;
    showSalePriceLabel?: boolean;
    extendedStyles?: ProductPriceStyles;
    isVarientProduct?: HasParentProductId;
    onClick?: React.EventHandler<React.MouseEvent>;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => ({
    currencySymbol: ownProps.currencySymbol ?? state.context.session.currency?.currencySymbol ?? "",
    canSeePrices: getSettingsCollection(state).productSettings.canSeePrices,
    variantSelection: state.pages.productDetails.variantSelection,
    variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductPriceStyles {
    wrapper?: any;
    priceLabelText?: TypographyPresentationProps;
    quoteMessageWrapper?: InjectableCss;
    quoteMessage?: RequiresQuoteMessageStyle;
    unitOfMeasure?: UnitOfMeasureStyles;
    priceWrapper?: InjectableCss;
    price?: PriceStyles;
    invalidPriceText?: TypographyPresentationProps;

    packWrapper?: InjectableCss;
    packLabelText?: TypographyPresentationProps;
    packDescriptionText?: TypographyPresentationProps;
    savingsMessageText?: TypographyPresentationProps;
    priceUomWrapper?: InjectableCss;
    viewProduct?: InjectableCss;
}

interface RequiresQuoteMessageStyle {
    text?: TypographyPresentationProps;
    tooltip?: TooltipPresentationProps;
}

interface PriceStyles {
    priceText?: TypographyPresentationProps;
    signInText?: TypographyPresentationProps;
    errorText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    unitOfMeasure?: UnitOfMeasureStyles;
    /**
     * @deprecated Use the `unitOfMeasure.unitOfMeasureText` property instead.
     */
    unitOfMeasureText?: TypographyPresentationProps;
    qtyPerBaseUnitOfMeasureText?: TypographyPresentationProps;
}

interface UnitOfMeasureStyles {
    unitOfMeasureText?: TypographyPresentationProps;
}

export const productPriceStyles: ProductPriceStyles = {
    viewProduct: {
        css: css`
            transition: all 200ms ease-in-out 0s;
            cursor: pointer;
            font-family: inherit;
            min-height: 38px;
            line-height: 1;
            backface-visibility: hidden;
            word-break: keep-all;
            padding: 0px 21px;
            background: rgb(88, 180, 108);
            border: 2px solid rgb(88, 180, 108);
            color: rgb(20, 20, 20);
            width: 55%;
            justify-content: center !important;
            margin: auto;
            margin-top: -4px;
            z-index: 1;
            &:hover {
                background: rgb(88, 180, 108);
                border: 2px solid rgb(88, 180, 108);
            }
            span {
                color: rgb(20, 20, 20) !important;
                font-size: 15px;
                font-weight: bold;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        text-transform: capitalize !important;
                    `,
                    css`
                        text-transform: capitalize !important;
                        width: 100%;
                    `,
                    css`
                        text-transform: capitalize !important;
                        width: 100%;
                    `,
                    css`
                        text-transform: capitalize !important;
                        width: 70%;
                    `,
                    css`
                        text-transform: capitalize !important;
                    `,
                ])}
        `,
    },
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            font-size: 12px;
            flex-direction: column;
        `,
    },
    priceLabelText: {
        css: css`
            text-transform: uppercase;
        `,
    },
    priceWrapper: {
        css: css`
            display: flex;
            [data-test-selector="productPrice_unitNetPrice"] {
                margin-left: 5px;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        display: flex;
                        span[data-test-selector="productPrice_unitNetPrice"] {
                            // margin-left: 5px;
                        }
                    `,
                    css`
                        span[data-test-selector="productPrice_unitNetPrice"] {
                            margin-left: 0;
                        }
                    `,
                    null,
                ])}
        `,
    },
    price: {
        priceText: {
            weight: "bold",
            css: css`
                color: #2450a8;
            `,
        },

        errorText: {
            weight: "bold",
            size: 15,
            css: css`
                color: #2450a8;
                padding-left: 10px;
            `,
        },
        realTimeText: {
            weight: "bold",
            css: css`
                /* width: [12, 12, 12, 12, 12], */
                display: inline-block;
                text-align: left;
                min-width: 25px;
                color: #2450a8;
                &::after {
                    overflow: hidden;
                    display: inline-block;
                    vertical-align: bottom;
                    animation: ellipsis steps(4, end) 900ms infinite 1s;
                    content: "\\2026";
                    width: 0;
                }

                @keyframes ellipsis {
                    to {
                        width: 1.25em;
                    }
                }
            `,
        },
        unitOfMeasure: {
            unitOfMeasureText: {
                css: css`
                    white-space: nowrap;
                    text-transform: uppercase;
                `,
            },
        },
    },
    invalidPriceText: {
        color: "danger",
    },
    packWrapper: {
        css: css`
            margin-bottom: 8px;
        `,
    },
    priceUomWrapper: {
        css: css`
            span:first-child {
                display: inline-flex !important;
            }
        `,
    },
    packLabelText: {
        weight: "normal",
    },
    savingsMessageText: {
        italic: true,
        size: "12px",
    },
    quoteMessage: {
        text: { weight: "bold" },
    },
};

const SectionWrapper = getStyledWrapper("section");

const ProductPrice: FC<Props> = ({
    product: pricingData,
    currencySymbol,
    canSeePrices,
    showLabel = true,
    showUnitOfMeasure = true,
    showPack = true,
    showSavings = false,
    showSavingsAmount = false,
    showSavingsPercent = false,
    showInvalidPriceMessage = false,

    extendedStyles,
    variantSelection,
    variantSelectionCompleted,
    product,
    onClick,
    showSalePriceLabel = true
}) => {
    const [styles] = React.useState(() => mergeToNew(productPriceStyles, extendedStyles));
    const [zeroPricing, setZeroPricing] = React.useState(false);
    const productContextModel = pricingData as ProductContextModel;
    const isVarient = productContextModel.product?.isVariantParent ?? false;
    const [isProductDetailsPage, setIsProductDetailsPage] = React.useState(false);
    const cartLineModel = pricingData as CartLineModel;
    const unitOfMeasureDescription =
        "product" in pricingData
            ? pricingData.product.unitOfMeasures?.find(o => o.unitOfMeasure === pricingData.productInfo.unitOfMeasure)
                  ?.description
            : pricingData.unitOfMeasureDescription;
    const unitOfMeasureDisplay =
        "product" in pricingData
            ? pricingData.product.unitOfMeasures?.find(o => o.unitOfMeasure === pricingData.productInfo.unitOfMeasure)
                  ?.unitOfMeasureDisplay
            : pricingData.unitOfMeasureDisplay;
    const quoteRequired = productContextModel.product
        ? productContextModel.product.quoteRequired
        : cartLineModel.quoteRequired;
    const qtyOrdered = productContextModel.product
        ? productContextModel.productInfo.qtyOrdered
        : cartLineModel.qtyOrdered;

    const pricing = "product" in pricingData ? pricingData.productInfo.pricing : pricingData.pricing;
    const allowZeroPricing =
        "product" in pricingData ? pricingData.product.allowZeroPricing : pricingData.allowZeroPricing;
    const isPromotionItem = "product" in pricingData ? false : pricingData.isPromotionItem;
    const isInvalidPrice =
        !quoteRequired &&
        !isPromotionItem &&
        !allowZeroPricing &&
        pricing?.unitNetPrice === 0 &&
        pricing?.unitRegularPrice === 0;

    const unitOfMeasure = unitOfMeasureDescription || unitOfMeasureDisplay;
    const showQtyPerBaseUnitOfMeasure =
        "baseUnitOfMeasure" in pricingData &&
        pricingData.unitOfMeasure.toLowerCase() !== pricingData.baseUnitOfMeasure.toLowerCase() &&
        pricingData.qtyPerBaseUnitOfMeasure > 0;
    const packDescription = "product" in pricingData ? pricingData.product.packDescription : undefined;

    const quoteStyles = styles.quoteMessage || {};
    const priceStyles = styles.price || {};

    const productDetailPath = "product" in product ? product.productInfo.productDetailPath : product.productUri;

    if (!canSeePrices) {
        return (
            <StyledWrapper {...styles.wrapper}>
                <Typography {...priceStyles.signInText} data-test-selector="productPrice_PricingMessage">
                    {siteMessage("Pricing_SignInForPrice")}
                </Typography>
            </StyledWrapper>
        );
    }

    if (typeof window !== "undefined") {
        setTimeout(() => {
            const productDetailsPageCheck = document.querySelector('[data-test-selector="page_ProductDetailsPage"]');
            if (
                productDetailsPageCheck &&
                "product" in pricingData &&
                (pricingData.productInfo.failedToLoadPricing || pricingData.productInfo.pricing?.actualPrice === 0)
            ) {
                const mainElement = document.querySelector("main");
                if (mainElement && mainElement.firstElementChild?.innerHTML === "0") {
                    mainElement.firstElementChild?.remove();
                }
                if (pricingData.productInfo.pricing?.actualPrice === 0 || pricingData.productInfo.failedToLoadPricing) {
                    setZeroPricing(true);
                } else {
                    setZeroPricing(false);
                }
                setIsProductDetailsPage(true);
            }
            const productListPageCheck = document.querySelector('[data-test-selector="page_ProductListPage"]');
            if (
                productListPageCheck &&
                "product" in pricingData &&
                pricingData.productInfo.pricing?.actualPrice === 0
            ) {
                if (pricingData.productInfo.pricing?.actualPrice === 0 || pricingData.productInfo.failedToLoadPricing) {
                    setZeroPricing(true);
                } else {
                    setZeroPricing(false);
                }
                setIsProductDetailsPage(false);
            }
        }, 0);
    }

    useEffect(() => {
        if (variantSelectionCompleted) {
            setZeroPricing(false);
        }
    }, [variantSelectionCompleted]);
    const salePriceLabel = showSalePriceLabel
        ? productContextModel.product?.salePriceLabel || cartLineModel?.salePriceLabel || translate("Sale Price:")
        : "";

    return (
        <SectionWrapper {...styles.wrapper} className="priceContainer">
            {quoteRequired && (
                <StyledWrapper {...styles.quoteMessageWrapper}>
                    <Typography {...quoteStyles.text} data-test-selector="quoteRequiredText">
                        {translate("Requires Quote")}
                    </Typography>
                    <Tooltip {...quoteStyles.tooltip} text={siteMessage("Rfq_TooltipMessage").toString()} />
                    <UnitOfMeasure
                        unitOfMeasure={unitOfMeasure}
                        showUnitOfMeasure={showUnitOfMeasure}
                        styles={styles.unitOfMeasure}
                    />
                </StyledWrapper>
            )}
            {!quoteRequired && (
                <>
                    <StyledWrapper {...styles.priceWrapper}>
                        <Typography data-test-selector="productPrice_label" {...styles.priceLabelText}>
                            {!zeroPricing ? translate("Price:") : ""}
                        </Typography>
                        {zeroPricing ? (
                            <SectionWrapper {...styles.wrapper}>
                                <Typography {...priceStyles.errorText} data-test-selector="productPrice_PricingMessage">
                                    {isProductDetailsPage
                                        ? isVarient
                                            ? siteMessage("RealTimePricing_Product_Is_Varient")
                                            : siteMessage("RealTimePricing_PriceLoadFailed")
                                        : isVarient
                                        ? siteMessage("RealTimePricing_Price_VariantMessage")
                                        : siteMessage("RealTimePricing_PriceLoadFailed")}
                                </Typography>
                                {!isProductDetailsPage && isVarient && (
                                    <Link
                                        {...styles.viewProduct}
                                        href={productDetailPath}
                                        alt="View Options"
                                        data-test-selector="viewOptionsLink"
                                        target="_self"
                                        onClick={onClick}
                                    >
                                        View Options
                                    </Link>
                                )}
                            </SectionWrapper>
                        ) : (
                            <StyledWrapper {...styles.priceUomWrapper}>
                                {pricing ? (
                                    <Typography
                                        {...priceStyles.priceText}
                                        data-test-selector="productPrice_unitNetPrice"
                                    >
                                        {getUnitNetPrice(pricing, qtyOrdered || 1).priceDisplay}
                                    </Typography>
                                ) : (
                                    <Typography
                                        {...priceStyles.realTimeText}
                                        data-test-selector="productPrice_unitNetPrice_CurrencySymbol"
                                    >
                                        {currencySymbol}
                                    </Typography>
                                )}
                                <UnitOfMeasure
                                    unitOfMeasure={unitOfMeasure}
                                    showUnitOfMeasure={showUnitOfMeasure}
                                    styles={styles.unitOfMeasure}
                                />
                                {isInvalidPrice && showInvalidPriceMessage && (
                                    <Typography as="p" {...styles.invalidPriceText}>
                                        {translate("Invalid Price")}
                                    </Typography>
                                )}
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                    {/* {showQtyPerBaseUnitOfMeasure && "baseUnitOfMeasure" in pricingData && (
                        <Typography {...priceStyles.qtyPerBaseUnitOfMeasureText}>
                            {`${pricingData.qtyPerBaseUnitOfMeasure} ${pricingData.baseUnitOfMeasureDisplay} / ${unitOfMeasure}`}
                        </Typography>
                    )} */}
                    {packDescription && showPack && (
                        <StyledWrapper {...styles.packWrapper}>
                            <Typography {...styles.packLabelText}>{translate("PACK")}:&nbsp;</Typography>
                            <Typography {...styles.packDescriptionText} {...priceStyles.priceText}>
                                {packDescription}
                            </Typography>
                        </StyledWrapper>
                    )}
                    {pricing && showSavings && (
                        <ProductPriceSavingsMessage
                            pricing={pricing}
                            qtyOrdered={qtyOrdered || 1}
                            showSavingsAmount={showSavingsAmount}
                            showSavingsPercent={showSavingsPercent}
                            currencySymbol={currencySymbol}
                            {...styles.savingsMessageText}
                        />
                    )}
                </>
            )}
        </SectionWrapper>
    );
};

interface UnitOfMeasureProps {
    unitOfMeasure?: string;
    showUnitOfMeasure?: boolean;
    styles?: UnitOfMeasureStyles;
}

const UnitOfMeasure = ({ unitOfMeasure, showUnitOfMeasure = true, styles }: UnitOfMeasureProps) => {
    return unitOfMeasure && showUnitOfMeasure ? (
        <Typography {...styles?.unitOfMeasureText} data-test-selector="productPrice_unitOfMeasureLabel">
            &nbsp;/&nbsp;{unitOfMeasure}
        </Typography>
    ) : null;
};

export default connect(mapStateToProps)(ProductPrice);
